
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getMainWindowPath,
  isMainWindow,
  isPopupWindow,
  isTauri,
  saveMainWindowPath,
  takeUpdateRestartPath,
} from '@/lib/utils/tauri';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/usePageRestoration');

/**
 * Hook to save the current route to Tauri storage (for main window only)
 * This enables restoring the last visited page when the app is reopened
 */
export function useRouteTracking() {
  const location = useLocation();
  const lastSavedPath = useRef<string | null>(null);

  useEffect(() => {
    // Only run in Tauri and not in popup windows
    if (!isTauri() || isPopupWindow()) {
      return;
    }

    const trackRoute = async () => {
      // Check if this is the main window
      const isMain = await isMainWindow();
      if (!isMain) return;

      const currentPath = location.pathname + location.search + location.hash;

      // Don't save if it's the same as the last saved path
      if (currentPath === lastSavedPath.current) return;

      // Save the current path
      await saveMainWindowPath(currentPath);
      lastSavedPath.current = currentPath;
    };

    trackRoute();
  }, [location.pathname, location.search, location.hash]);
}

/**
 * Hook to restore the last visited page on app startup (for main window only)
 * Returns true if a restore navigation was initiated
 */
export function usePageRestore(): boolean {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRestored = useRef(false);
  const didNavigate = useRef(false);
  // React Router immediately redirects `/` to `/organizations`. Preserve the
  // true startup entry so that redirect cannot outrun restoration after a
  // relaunch (the root cause of update restarts landing on Organizations).
  const startupPath = useRef(location.pathname);

  useEffect(() => {
    // Only run once, only in Tauri, and only if not already restored
    if (hasRestored.current || !isTauri() || isPopupWindow()) {
      return;
    }

    const restore = async () => {
      // Check if this is the main window
      const isMain = await isMainWindow();
      if (!isMain) return;

      hasRestored.current = true;

      try {
        // Consume this even when an explicit startup route wins, so an update
        // return path can never leak into a later, unrelated launch.
        const updateRestartPath = await takeUpdateRestartPath();

        const startedAtRoot =
          startupPath.current === '/' || startupPath.current === '';
        const isStartupPlaceholder =
          location.pathname === '/' ||
          location.pathname === '' ||
          location.pathname === '/organizations';

        // Only restore when this webview really started at the root. Explicit
        // deep links (including an intentional /organizations launch) win.
        if (!startedAtRoot || !isStartupPlaceholder) {
          return;
        }

        const isSafeInternalPath = (path: string | null | undefined) =>
          Boolean(
            path &&
              path.startsWith('/') &&
              !path.startsWith('//') &&
              !path.includes('\\') &&
              path !== '/',
          );
        const isSafeUpdateRestartPath = isSafeInternalPath(updateRestartPath);
        const savedPath = isSafeUpdateRestartPath
          ? updateRestartPath
          : await getMainWindowPath();

        if (savedPath && isSafeInternalPath(savedPath)) {
          logger.info(
            `${isSafeUpdateRestartPath ? 'Restoring page after update' : 'Restoring last page'}: ${savedPath}`
          );
          didNavigate.current = true;
          navigate(savedPath, { replace: true });
        }
      } catch (error) {
        logger.error('Failed to restore page:', error);
      }
    };

    // Small delay to ensure the app is fully loaded
    const timeout = setTimeout(restore, 100);
    return () => clearTimeout(timeout);
  }, [navigate, location.pathname]);

  return didNavigate.current;
}
